<script setup lang="ts">
import { ChevronRightIcon } from "@heroicons/vue/24/solid";
import type { IconsType } from "~/types";
import { useDashboardStore } from "~/stores/dashboard";

const mainStore = useMainStore();
const { dashboardNavigationMobile } = storeToRefs(mainStore);
const { status, signOut } = useAuth();
const { globalSettings } = storeToRefs(mainStore);

const navigation = computed(() => globalSettings.value?.header);

const showDashboardNav = ref(true);
const router = useRouter();
const route = useRoute();
const dashboardStore = useDashboardStore();

function toggleDashboardNav() {
  showDashboardNav.value = !showDashboardNav.value;
}

const login = () => {
  mainStore.toggleMobileNavigation();
  // Navigate to login page
  navigateTo("/account");
};

// Function to handle navigation to dashboard pages
function navigateToDashboard(query: string) {
  // Close mobile navigation
  mainStore.toggleMobileNavigation();

  // Check if we're navigating to the same page
  if (route.query.view === query.replace("?view=", "")) {
    // Update the refresh timestamp in the store
    dashboardStore.refreshView(query.replace("?view=", ""));
    return;
  }

  // Navigate to the page normally
  navigateTo(`/account/dashboard${query}`);
}

// Function to navigate to a page
function navigateToPage(link: any) {
  // Close mobile navigation
  mainStore.toggleMobileNavigation();

  // BMPAAS-6370: the "#chat-with-us" marker opens the Genesys chat widget instead of
  // navigating. The mobile menu renders links via JS (href="javascript:void(0)"), so the
  // global click delegate in Header.vue that handles desktop <a href="#chat-with-us">
  // never fires here — handle the marker explicitly.
  const chatUrl = typeof link?.url === "string" ? link.url : "";
  if (chatUrl.endsWith("#chat-with-us")) {
    const w = window as unknown as { Genesys?: (...args: unknown[]) => void };
    if (typeof window !== "undefined" && typeof w.Genesys === "function") {
      w.Genesys("command", "Messenger.open");
    } else {
      console.warn("Genesys chat widget not available");
    }
    return;
  }

  // Handle different link types
  if (link) {
    if (link.type === "custom") {
      // Handle custom URL links
      if (link.url) {
        // Open URL in the specified target or _self by default
        window.open(link.url, link.target || "_self");
      }
    } else if (link.type === "url") {
      // Check if it's an external URL (starts with http or https)
      if (
        link.url &&
        typeof link.url === "string" &&
        (link.url.startsWith("http://") || link.url.startsWith("https://"))
      ) {
        // For external links, convert 'blank' target to '_blank'
        const target = link.target === "blank" ? "_blank" : (link.target || "_self");
        // Open external URL in the specified target
        window.open(link.url, target);
      } else {
        // Ensure URL is a string before navigation
        const safeUrl = typeof link.url === "string" ? link.url : "";
        console.log(`Sanitized URL from '${link.url}' to '${safeUrl}'`);
        // Navigate to internal URL safely
        navigateTo(`/${safeUrl}`);
      }
    } else if (link.type === "page") {
      // Ensure slug is a string
      const safeSlug =
        typeof link.page?.slug === "string" ? link.page.slug : "";
      console.log(
        `Sanitized page slug from '${link.page?.slug}' to '${safeSlug}'`
      );
      // Navigate to page using sanitized slug
      navigateTo(`/${safeSlug}`);
    }
  }
}

// Toggle submenu visibility
const expandedSubmenus = ref<number[]>([]);

function toggleSubmenu(index: number) {
  const position = expandedSubmenus.value.indexOf(index);
  if (position > -1) {
    // Remove from array if already exists
    expandedSubmenus.value.splice(position, 1);
  } else {
    // Add to array if not exists
    expandedSubmenus.value.push(index);
  }
}

function isSubmenuExpanded(index: number): boolean {
  return expandedSubmenus.value.includes(index);
}

async function logout() {
  await signOut();
  // Close mobile navigation
  mainStore.toggleMobileNavigation();
  // Navigate to home page (match desktop behavior)
  navigateTo("/");
}

// Redirect to /account if not authenticated
watchEffect(() => {
  if (status.value === "unauthenticated") {
    // navigateTo("/account");
  }
});
</script>

<template>
  <div class="mobile_menu" style="z-index: 99999">
    <div class="top">
      <button @click="mainStore.toggleMobileNavigation">
        Back
        <ChevronRightIcon class="w-5" />
      </button>
    </div>
    <div class="nav">
      <ul>
        <li v-for="(nav, navIndex) in navigation" :key="navIndex">
          <!-- Main navigation item -->
          <div class="flex items-center justify-between">
            <!-- If has subitems, show toggle button -->
            <a
              v-if="nav.subitems && nav.subitems.length > 0"
              class="text-[16px] flex-grow"
              href="javascript:void(0)"
              @click="toggleSubmenu(navIndex)"
            >
              {{ nav.title }}
            </a>
            <!-- If no subitems, show regular link -->
            <a
              v-else
              class="text-[16px]"
              href="javascript:void(0)"
              @click="navigateToPage(nav.link)"
            >
              {{ nav.title }}
            </a>

            <!-- Toggle arrow for items with subitems -->
            <button
              v-if="nav.subitems && nav.subitems.length > 0"
              class="p-2"
              @click="toggleSubmenu(navIndex)"
            ></button>
          </div>

          <!-- Submenu items -->
          <ul
            v-if="nav.subitems && nav.subitems.length > 0"
            class="pl-4 mt-2 space-y-2 overflow-hidden transition-all duration-200"
            :class="{ 'h-0 mt-0': !isSubmenuExpanded(navIndex) }"
          >
            <li v-for="(subitem, subIndex) in nav.subitems" :key="subIndex">
              <a
                class="text-[14px] block py-1"
                href="javascript:void(0)"
                @click="navigateToPage(subitem)"
              >
                {{ subitem.linkText }}
              </a>
            </li>
          </ul>
        </li>

        <!-- My Account list item with toggle functionality -->

        <li
          class="!border-t border-white/20 pt-2 mt-2 cursor-pointer"
          @click="toggleDashboardNav"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i
                class="material-icons !text-[24px] text-white align-text-bottom md:align-baseline"
                >dashboard</i
              >
              <span class="text-white text-[16px]">My Account</span>
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div
      class="dashboard_nav"
      v-if="showDashboardNav && status === 'authenticated'"
    >
      <ul>
        <li
          :style="
            index > 0 ? { borderTop: '1px solid rgba(0, 0, 0, 0.05)' } : {}
          "
          v-for="(nav, index) in dashboardNavigationMobile"
          :key="index"
        >
          <a
            href="javascript:void(0)"
            class="flex align-center gap-2"
            v-if="nav.query"
            @click="navigateToDashboard(nav.query)"
          >
            <i
              class="material-icons !text-[24px] align-text-bottom md:align-baseline text-white"
              >{{ nav.iconName }}</i
            >
            <span class="capitalize">{{ nav.title }}</span>
          </a>
          <button
            class="flex align-center gap-2"
            v-else
            @click="logout()"
          >
            <i
              class="material-icons !text-[24px] align-text-bottom md:align-baseline text-white"
              >{{ nav.iconName }}</i
            >
            <span class="capitalize">{{ nav.title }}</span>
          </button>
        </li>
      </ul>
    </div>
    <div v-if="showDashboardNav && status === 'unauthenticated'">
      <div class="pl-10" @click="login">
        <i class="fa fa-sign-in">&nbsp;</i>
        <span class="pl-1">login</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mobile_menu {
  @apply fixed bg-primary w-[80%] right-0 top-0 
    rounded-sm border border-white shadow-[0px_0px_30px_0px_#0000000D]
    h-[calc(100vh-60px)];
  z-index: 99999 !important; /* Ensure mobile menu appears above all elements */
  color: white;
  overflow-y: auto; /* Allow scrolling if content is too tall */

  .top {
    @apply flex justify-end text-white border-b-[1px] border-white py-4 px-4;
    button {
      @apply flex text-white font-bold text-lg items-center;
    }
  }

  .nav {
    @apply px-6 py-6 flex flex-col gap-4 text-white;
    ul {
      @apply space-y-5;

      li {
        :deep() {
          a {
            @apply text-white hover:text-white/80 transition-colors;
          }
        }
      }
    }
  }

  .dashboard_nav {
    @apply px-6;

    ul {
      @apply pl-11;
      li {
        @apply text-white p-2 text-sm;
      }
    }
  }
}
</style>
